/**
 * City-specific Google Tag Manager utility
 * BLR (wf1, wf2, blr) → GTM-T5P6CWHW
 * HYD (fd1, hyd)       → GTM-K5HPDHGQ
 * Default (no city)     → Both containers
 */
import { useEffect, useRef } from 'react';

const GTM_BLR = 'GTM-T5P6CWHW';
const GTM_HYD = 'GTM-K5HPDHGQ';

const BLR_SLUGS = new Set(['wf1', 'wf2', 'blr']);
const HYD_SLUGS = new Set(['fd1', 'hyd']);

function getGTMIds(studioSlug) {
  const slug = (studioSlug || '').toLowerCase();
  if (BLR_SLUGS.has(slug)) return [GTM_BLR];
  if (HYD_SLUGS.has(slug)) return [GTM_HYD];
  return [GTM_BLR, GTM_HYD]; // default: both
}

function injectGTMScript(gtmId) {
  const existingScript = document.querySelector(`script[src*="${gtmId}"]`);
  if (existingScript) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.insertBefore(script, document.head.firstChild);
}

function injectGTMNoscript(gtmId) {
  const noscriptId = `gtm-noscript-${gtmId}`;
  if (document.getElementById(noscriptId)) return;

  const noscript = document.createElement('noscript');
  noscript.id = noscriptId;
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

/**
 * Hook to load the correct GTM container(s) based on studioSlug
 * @param {string} studioSlug - e.g. 'wf1', 'fd1', 'hyd', 'blr', or undefined
 */
export function useGTM(studioSlug) {
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;

    const ids = getGTMIds(studioSlug);
    ids.forEach(id => {
      injectGTMScript(id);
      injectGTMNoscript(id);
    });
  }, [studioSlug]);
}

/**
 * Push AddToWishlist event to dataLayer on CTA clicks
 * @param {string} formLocation - e.g. 'hero', 'urgency', 'footer', 'header', 'pricing', 'finalcta'
 */
export function pushCTAEvent(formLocation) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'AddToWishlist',
    formLocation: formLocation || 'cta',
  });
}
